import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useUserProfile } from '@/hooks/useUserProfile';
import { AvatarCreator } from '@/components/AvatarCreator';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { DEFAULT_AVATAR_CONFIG, generateAvatarFromUsername, type AvatarConfig } from '@/lib/avatar-config';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Palette } from 'lucide-react';

export default function AvatarEditor() {
  // SEO Configuration
  useSEO({
    title: "Customize Your Avatar - ChessHatch Profile Settings",
    description: "Personalize your ChessHatch profile with custom avatar creation. Customize colors, styles, and express your chess personality with unique avatar designs.",
    keywords: "chess avatar, profile customization, chess profile, avatar creator, chess identity"
  });

  const navigate = useNavigate();
  const { profile, updateUsername, updateAvatar } = useUserProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState(profile?.username || '');
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const handleUsernameUpdate = async () => {
    if (!newUsername || newUsername === profile?.username) {
      setIsEditing(false);
      return;
    }

    if (newUsername.length < 3 || newUsername.length > 20) {
      setUsernameError('Username must be between 3-20 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) {
      setUsernameError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setIsLoading(true);
    const { error } = await updateUsername(newUsername);
    
    if (error) {
      setUsernameError(error);
    } else {
      toast.success('Username updated!');
      setIsEditing(false);
      setUsernameError('');
    }
    setIsLoading(false);
  };

  const handleAvatarUpdate = async (config: AvatarConfig) => {
    const { error } = await updateAvatar('custom', config);
    
    if (error) {
      toast.error('Failed to update avatar');
    } else {
      toast.success('Avatar updated!');
      navigate('/');
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Profile not found</h2>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teen-blue/20 via-background to-teen-purple/20 p-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Profile Settings</h1>
            <p className="text-muted-foreground">Customize your gaming identity</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back Home
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Profile */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Current Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <AvatarDisplay
                  username={profile.username || 'Player'}
                  avatarConfig={profile.avatar_config}
                  avatarUrl={profile.avatar_url}
                  size="xl"
                  showAnimation={true}
                  className="mx-auto mb-4"
                />
                <div className="space-y-2">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        value={newUsername}
                        onChange={(e) => {
                          setNewUsername(e.target.value);
                          setUsernameError('');
                        }}
                        placeholder="Enter new username"
                        className={usernameError ? 'border-destructive' : ''}
                      />
                      {usernameError && (
                        <p className="text-sm text-destructive">{usernameError}</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleUsernameUpdate}
                          disabled={isLoading}
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setNewUsername(profile.username || '');
                            setUsernameError('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="font-semibold text-lg">{profile.username}</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Username
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-medium">Stats</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="font-bold text-lg">{profile.elo_rating}</div>
                    <div className="text-muted-foreground">ELO Rating</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="font-bold text-lg">{profile.games_played}</div>
                    <div className="text-muted-foreground">Games Played</div>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  const config = generateAvatarFromUsername(profile.username || 'Player');
                  handleAvatarUpdate(config);
                }}
              >
                <Palette className="w-4 h-4 mr-2" />
                Generate New Avatar
              </Button>
            </CardContent>
          </Card>

          {/* Avatar Editor */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Avatar Customizer</CardTitle>
              </CardHeader>
              <CardContent>
                <AvatarCreator
                  initialConfig={profile.avatar_config || DEFAULT_AVATAR_CONFIG}
                  username={profile.username || 'Player'}
                  onSave={handleAvatarUpdate}
                  onCancel={() => navigate('/')}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}